import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Button, IconButton, Chip,
  Divider, Paper, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Alert,
  Snackbar, List, ListItem, ListItemText, ListItemIcon,
  Drawer, Badge, Stack, Fab, useTheme, useMediaQuery,
  LinearProgress, Fade, Avatar, ListSubheader
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  VolumeUp as VolumeUpIcon,
  Translate as TranslateIcon,
  Bookmark as BookmarkIcon,
  BookmarkBorder as BookmarkBorderIcon,
  ListAlt as VocabularyIcon,
  Check as CheckIcon,
  ArrowForward as NextIcon,
  Close as CloseIcon,
  PlayCircle as PlayIcon,
  Menu as MenuIcon,
  School as LearnIcon,
  EmojiEvents as CompletedIcon,
  SentimentSatisfied as SmileyIcon,
  SentimentDissatisfied as SadIcon,
  Help as HelpIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from "../context/LanguageContext";
import useAudioPlayer from '../hooks/useAudioPlayer';
import AudioPlayer from '../components/audioPlayer';
import WordDialog from '../components/WordDialog';
import useWordSelection from "../hooks/useWordSelection";
import { useSettings } from '../context/SettingsContext';
import VoiceSelector from '../components/VoiceSelector';
import AnnotatedStoryText from '../components/AnnotatedStoryText';

// Knowledge level options for vocabulary
const knowledgeLevels = [
  { value: 1, label: "Don't Know", color: 'error', icon: <SadIcon /> },
  { value: 2, label: 'Learning', color: 'warning', icon: <HelpIcon /> },
  { value: 3, label: 'Know Well', color: 'success', icon: <SmileyIcon /> }
];

const BreadcrumbsStoryDetail = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedLanguage } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Audio player state
  const { audioPlayerState, openAudioPlayer, closeAudioPlayer } = useAudioPlayer();

  // State
  const [story, setStory] = useState(null);
  const [glossary, setGlossary] = useState({});
  // Word matching (per-sentence English + tappable words). When present it
  // replaces the older glossary-based highlighting.
  const [annotations, setAnnotations] = useState(null);
  const [content, setContent] = useState(null);
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTranslation, setShowTranslation] = useState(false);
  const [highlightedWords, setHighlightedWords] = useState([]);
  const [selectedWord, setSelectedWord] = useState(null);
  const [wordDialogOpen, setWordDialogOpen] = useState(false);
  const [vocabulary, setVocabulary] = useState([]);
  const [vocabDrawerOpen, setVocabDrawerOpen] = useState(false);
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [nextStory, setNextStory] = useState(null);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [userProgress, setUserProgress] = useState({
    status: 'not_started',
    started_at: null,
    completed_at: null
  });
  const [translationDialogOpen, setTranslationDialogOpen] = useState(false);

  const {
    ttsVoiceGender: defaultGender,
    ttsVoiceQualityBreadcrumbs: defaultQuality
  } = useSettings();

  const [voiceGender, setVoiceGender] = useState(defaultGender || 'Female');
  const [voiceQuality, setVoiceQuality] = useState(defaultQuality || 'medium');
  const [audioGenerating, setAudioGenerating] = useState(false);
  const [showVoiceSettings, setShowVoiceSettings] = useState(false);

  useEffect(() => {
    setVoiceGender(defaultGender || 'Female');
    setVoiceQuality(defaultQuality || 'medium');
  }, [defaultGender, defaultQuality]);

  // Fetch story data
  useEffect(() => {
    fetchStoryData();
  }, [storyId, selectedLanguage]);

  // Fetch vocabulary when story loads
  useEffect(() => {
    if (story && selectedLanguage) {
      fetchVocabulary(selectedLanguage);
    }
  }, [story, selectedLanguage]);

  const fetchStoryData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.breadcrumbs.getStory(storyId, selectedLanguage);

      const storyData = response.data.story;
      setStory(storyData);
      setPath(response.data.path);

      // IMPORTANT: Fix the content/translation based on the language
      const contentData = response.data.content;

      // The backend logic already handles the swap for non-English languages
      // We just need to use the data as provided
      setContent(contentData);
      setAnnotations(response.data.annotations || null);

      // Set glossary and highlighted words
      if (response.data.glossary) {
        console.log('GLOSSARY');
        console.log(response.data.glossary)
        setGlossary(response.data.glossary);
        const wordsToHighlight = Object.keys(response.data.glossary);
        setHighlightedWords(wordsToHighlight);
      }

      // Set user progress
      if (response.data.user_progress) {
        setUserProgress(response.data.user_progress);
      }

      // Get next story if available
      if (response.data.next_story) {
        setNextStory(response.data.next_story);
      } else if (storyData.next_story_id) {
        setNextStory({ story_id: storyData.next_story_id });
      }

      // Update status to in_progress if not already completed
      if (!response.data.user_progress ||
          (response.data.user_progress?.status !== 'completed' &&
           response.data.user_progress?.status !== 'in_progress')) {
        updateStoryProgress('in_progress');
      }
    } catch (error) {
      console.error('Error fetching story:', error);
      showNotification('Error loading story', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch vocabulary
  const fetchVocabulary = async (languageCode) => {
    try {
      const response = await apiClient.vocabulary.getVocabularyItems({ language_code: languageCode });
      setVocabulary(response.data.vocabulary || []);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
    }
  };

  // Update story progress
  const updateStoryProgress = async (status) => {
    try {
      await apiClient.breadcrumbs.updateStoryProgress(storyId, {
        status,
        language_code: selectedLanguage
      });
      setUserProgress(prev => ({ ...prev, status }));

      if (status === 'completed') {
        setCompletionDialogOpen(true);
      }
    } catch (error) {
      console.error('Error updating story progress:', error);
      if (status === 'completed') {
        showNotification('Error saving progress', 'error');
      }
    }
  };

  // Mark story as completed
  const markAsCompleted = () => {
    updateStoryProgress('completed');
  };

  // Play audio
  const handlePlayAudio = () => {
    if (!content || !content.audio_file) return;

    // Get audio URL using the apiClient method
    const audioUrl = apiClient.audio.getAudioUrl(content.audio_file);

    // Open audio player with proper parameters
    openAudioPlayer(audioUrl, `${story?.title} - Audio`);
  };

  // Toggle translation dialog
  const toggleTranslation = () => {
    setTranslationDialogOpen(!translationDialogOpen);
  };

  // Navigate back to path
  const handleBackToPath = () => {
    if (path?.path_id) {
      navigate(`/breadcrumbs/paths/${path.path_id}`);
    } else {
      navigate('/breadcrumbs');
    }
  };

  // Go to next story
  const handleNextStory = () => {
    if (nextStory?.story_id) {
      navigate(`/breadcrumbs/stories/${nextStory.story_id}`);
      setCompletionDialogOpen(false);
    } else if (story?.next_story_id) {
      navigate(`/breadcrumbs/stories/${story.next_story_id}`);
      setCompletionDialogOpen(false);
    } else {
      handleBackToPath();
    }
  };

  // Toggle vocabulary drawer
  const handleToggleVocabDrawer = () => {
    setVocabDrawerOpen(!vocabDrawerOpen);
  };

  // Open word dialog
  const handleOpenWordDialog = (wordData) => {
    if (typeof wordData === 'string') {
      setSelectedWord({
        word: wordData,
        translation: glossary[wordData] || '',
        language_code: selectedLanguage,
        knowledge_level: 1
      });
    } else {
      setSelectedWord(wordData);
    }
    setWordDialogOpen(true);
  };

  // Close word dialog
  const handleCloseWordDialog = () => {
    setWordDialogOpen(false);
    setSelectedWord(null);
  };

  // Save word to vocabulary
  const handleSaveWord = async (wordData) => {
    try {
      if (wordData.id) {
        // Update existing word
        await apiClient.vocabulary.updateVocabularyItem(wordData.id, wordData);
        setVocabulary(prev =>
          prev.map(item => item.id === wordData.id ? { ...item, ...wordData } : item)
        );
        showNotification('Word updated successfully', 'success');
      } else {
        // Add new word
        const response = await apiClient.vocabulary.addVocabularyItem({
          ...wordData,
          language_code: selectedLanguage
        });
        setVocabulary(prev => [...prev, response.data.vocabulary_item]);
        showNotification('Word added to vocabulary', 'success');
      }
      handleCloseWordDialog();
      fetchVocabulary(selectedLanguage);
    } catch (error) {
      console.error('Error saving word:', error);
      if (error.response?.data?.error?.includes('already in your vocabulary')) {
        showNotification('This word is already in your vocabulary', 'info');
      } else {
        showNotification('Failed to save word', 'error');
      }
    }
  };

  // Show notification
  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  // Normalize apostrophes for matching
  const normalizeApostrophes = (text) => {
    if (!text) return text;
    return text.replace(/['']/g, "'");
  };

  // Render story content with highlighted vocabulary words
  const renderContent = (text) => {
    if (!text) return text;

    // Split text to highlight vocabulary words and preserve punctuation
    const words = text.split(/(\s+|[.,;:!?"()[\]{}](?:\s|$))/);

    return words.map((word, index) => {
      // Skip empty words, spaces, and lone punctuation
      if (!word.trim() || /^[\s.,;:!?'"()[\]{}]+$/.test(word)) {
        return <React.Fragment key={index}>{word}</React.Fragment>;
      }

      // Clean the word
      const cleanWord = word.trim().replace(/^[.,;:!?"()[\]{}]+|[.,;:!?"()[\]{}]+$/g, '');
      if (!cleanWord) {
        return <React.Fragment key={index}>{word}</React.Fragment>;
      }

      // Normalize for matching
      const normalizedClean = normalizeApostrophes(cleanWord);

      // Check if word is in highlighted words from glossary
      const isHighlighted = highlightedWords.some(v =>
        normalizeApostrophes(v).toLowerCase() === normalizedClean.toLowerCase()
      );

      // Check if word is in user vocabulary
      const inVocabulary = vocabulary.some(v =>
        normalizeApostrophes(v.word).toLowerCase() === normalizedClean.toLowerCase()
      );

      // Get translation from glossary
      let glossaryTranslation = null;
      if (glossary) {
        const matchingKey = Object.keys(glossary).find(key =>
          normalizeApostrophes(key).toLowerCase() === normalizedClean.toLowerCase()
        );
        if (matchingKey) {
          glossaryTranslation = glossary[matchingKey];
        }
      }

      if (isHighlighted || inVocabulary) {
        let color = 'primary.main';
        if (inVocabulary) {
          const vocabWord = vocabulary.find(v =>
            normalizeApostrophes(v.word).toLowerCase() === normalizedClean.toLowerCase()
          );
          if (vocabWord) {
            switch (vocabWord.knowledge_level) {
              case 1: color = 'error.main'; break;
              case 2: color = 'warning.main'; break;
              case 3: color = 'success.main'; break;
            }
          }
        }

        return (
          <Box
            key={index}
            component="span"
            onClick={() => handleOpenWordDialog(cleanWord)}
            sx={{
              cursor: 'pointer',
              color: color,
              fontWeight: isHighlighted ? 'medium' : 'normal',
              borderBottom: isHighlighted && !inVocabulary ? `1px dotted ${color}` : 'none',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            <Tooltip title={glossaryTranslation || 'Click to add to vocabulary'}>
              <span>{word}</span>
            </Tooltip>
          </Box>
        );
      }

      return word;
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      position: 'relative',
      mx: isMobile ? -3 : 0, // Remove horizontal margin on mobile to match LearningSessionPage
      display: 'flex',
      flexDirection: 'column',
      minHeight: isMobile ? 'calc(100vh - 56px)' : '100vh', // Account for bottom nav
      bgcolor: 'background.default'
    }}>
      {/* Header - matching LearningSessionPage style */}
      <Card sx={{
        mb: 2,
        borderRadius: isMobile ? 0 : 1,
        bgcolor: 'background.paper'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton onClick={handleBackToPath} sx={{ mr: 1 }}>
                <ArrowBackIcon />
              </IconButton>
              <Box>
                {story?.section && (
                  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                    {story.section}
                  </Typography>
                )}
                <Typography variant="h5">
                  {story?.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={selectedLanguage?.toUpperCase()}
                    size="small"
                    color="primary"
                    sx={{ mr: 1, mb: isMobile ? 0.5 : 0 }}
                  />
                  <Chip
                    label={story?.chapter_number && story?.chapter_count
                      ? `Chapter ${story.chapter_number} of ${story.chapter_count}`
                      : `Chapter ${(story?.order_index ?? 0) + 1}`}
                    size="small"
                    color="secondary"
                    sx={{ mr: 1, mb: isMobile ? 0.5 : 0 }}
                  />
                  <Chip
                    label={path?.title}
                    size="small"
                    variant="outlined"
                    sx={{ mb: isMobile ? 0.5 : 0 }}
                    icon={<LearnIcon />}
                  />
                </Box>
              </Box>
            </Box>

            <Box>
              <Tooltip title="View Vocabulary">
                <Badge
                  badgeContent={vocabulary.length}
                  color="primary"
                  overlap="circular"
                >
                  <IconButton
                    onClick={handleToggleVocabDrawer}
                    color={vocabDrawerOpen ? "primary" : "default"}
                  >
                    <VocabularyIcon />
                  </IconButton>
                </Badge>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Content Container - matching LearningSessionPage style */}
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 1 : 2,
          flex: "1 1 auto",
          overflowY: "auto",
          mb: 1,
          bgcolor: 'background.default',
          borderRadius: isMobile ? 0 : undefined,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            padding: 2,
            paddingBottom: isMobile ? '100px' : '60px'
          }}
        >
          {/* Story content — constrained, centered reading column */}
          <Paper
            elevation={1}
            sx={{
              p: isMobile ? 3 : { sm: 4, md: 6 },
              bgcolor: 'background.paper',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              mb: 3,
              width: '100%',
              maxWidth: 720,
              mx: 'auto'
            }}
          >
            {/* Reading toolbar */}
            {(content?.audio_file || content?.translation) && (
              <Box sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                mb: 2,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}>
                {content?.audio_file && (
                  <Button
                    onClick={handlePlayAudio}
                    startIcon={<VolumeUpIcon />}
                    size="small"
                    variant="outlined"
                  >
                    Listen
                  </Button>
                )}
                {content?.translation && (
                  <Button
                    onClick={toggleTranslation}
                    startIcon={<TranslateIcon />}
                    size="small"
                    variant="outlined"
                  >
                    Translation
                  </Button>
                )}
              </Box>
            )}

            {annotations ? (
              <AnnotatedStoryText
                annotations={annotations}
                vocabulary={vocabulary}
                languageCode={selectedLanguage}
                onMoreWord={(w) => handleOpenWordDialog({ ...w, language_code: selectedLanguage, knowledge_level: 1 })}
                typographySx={{
                  fontSize: isMobile ? '1.05rem' : '1.2rem',
                  lineHeight: 1.95,
                  letterSpacing: '0.01em',
                  color: 'text.primary',
                  fontFamily: '"Georgia", "Times New Roman", serif',
                }}
              />
            ) : (
            <Typography
              variant="body1"
              sx={{
                fontSize: isMobile ? '1.05rem' : '1.2rem',
                lineHeight: 1.95,
                letterSpacing: '0.01em',
                color: 'text.primary',
                fontFamily: '"Georgia", "Times New Roman", serif',
                whiteSpace: 'pre-wrap'
              }}
            >
              {renderContent(content?.content || 'Loading content...')}
            </Typography>
            )}
          </Paper>

          {/* Completion section */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: 'background.paper',
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            {userProgress.status === 'completed' ? (
              <>
                <Chip
                  icon={<CheckIcon />}
                  label="Story Completed"
                  color="success"
                  sx={{ mb: 3 }}
                />
                <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
                  {(nextStory || story?.next_story_id) && (
                    <Button
                      variant="contained"
                      onClick={handleNextStory}
                      startIcon={<NextIcon />}
                    >
                      Next Story
                    </Button>
                  )}
                  <Button
                    variant="outlined"
                    onClick={handleBackToPath}
                    startIcon={<ArrowBackIcon />}
                  >
                    Back to Path
                  </Button>
                </Stack>
                {!(nextStory || story?.next_story_id) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    You've completed all stories in this path!
                  </Typography>
                )}
              </>
            ) : (
              <Button
                variant="contained"
                size="large"
                startIcon={<CheckIcon />}
                onClick={markAsCompleted}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  bgcolor: 'success.main',
                  '&:hover': {
                    bgcolor: 'success.dark'
                  }
                }}
              >
                Mark as Completed
              </Button>
            )}
          </Paper>
        </Box>
      </Paper>

      {/* Vocabulary Drawer */}
      <Drawer
        anchor="right"
        open={vocabDrawerOpen}
        onClose={handleToggleVocabDrawer}
        sx={{
          width: { xs: '90%', sm: 320 },
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: { xs: '90%', sm: 320 },
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Vocabulary</Typography>
          <IconButton onClick={handleToggleVocabDrawer}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        <List
          subheader={
            <ListSubheader component="div">
              {selectedLanguage?.toUpperCase()} Vocabulary ({vocabulary.length})
            </ListSubheader>
          }
        >
          {vocabulary.length === 0 ? (
            <ListItem>
              <ListItemText
                primary="No vocabulary items yet"
                secondary="Click on highlighted words in the story to add them to your vocabulary"
              />
            </ListItem>
          ) : (
            knowledgeLevels.map(level => {
              const wordsInLevel = vocabulary.filter(
                word => word.knowledge_level === level.value
              );

              if (wordsInLevel.length === 0) return null;

              return (
                <React.Fragment key={level.value}>
                  <ListSubheader sx={{ bgcolor: `${level.color}.light`, color: 'text.primary' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {React.cloneElement(level.icon, { fontSize: 'small', sx: { mr: 1 } })}
                      {level.label} ({wordsInLevel.length})
                    </Box>
                  </ListSubheader>

                  {wordsInLevel.map(word => (
                    <ListItem
                      key={word.id}
                      button
                      onClick={() => handleOpenWordDialog(word)}
                    >
                      <ListItemText
                        primary={word.word}
                        secondary={word.translation}
                      />
                    </ListItem>
                  ))}
                </React.Fragment>
              );
            })
          )}
        </List>
      </Drawer>

      {/* Translation Dialog */}
      <Dialog
        open={translationDialogOpen}
        onClose={() => setTranslationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Translation</Typography>
          <IconButton onClick={() => setTranslationDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {/* Original Text
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                {selectedLanguage?.toUpperCase()} Text
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {content?.content}
                </Typography>
              </Paper>
            </Box>*/}

            {/* Translation */}
            <Box>
              <Typography variant="subtitle2" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                English Translation
              </Typography>
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'primary.lighter', border: '1px solid', borderColor: 'primary.light' }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
                  {content?.translation}
                </Typography>
              </Paper>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTranslationDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Completion Dialog */}
      <Dialog
        open={completionDialogOpen}
        onClose={() => setCompletionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'success.main',
              mx: 'auto',
              mb: 2
            }}
          >
            <CompletedIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" gutterBottom>
            Great Job!
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You've completed "{story?.title}"
          </Typography>

          {(nextStory?.title || story?.next_story_id) && (
            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
              {nextStory?.title ?
                `Ready for the next story: ${nextStory.title}` :
                'Ready to continue to the next story!'
              }
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, flexDirection: 'column', gap: 1 }}>
          <Stack direction="row" spacing={2}>
            {(nextStory || story?.next_story_id) && (
              <Button
                variant="contained"
                onClick={handleNextStory}
                startIcon={<NextIcon />}
                size="large"
              >
                Continue to Next Story
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={handleBackToPath}
              startIcon={<ArrowBackIcon />}
            >
              Back to Path
            </Button>
          </Stack>
          <Button
            size="small"
            onClick={() => setCompletionDialogOpen(false)}
            sx={{ mt: 1 }}
          >
            Stay on This Story
          </Button>
        </DialogActions>
      </Dialog>

      {/* Word Dialog */}
      <WordDialog
        open={wordDialogOpen}
        onClose={handleCloseWordDialog}
        word={selectedWord}
        onSave={handleSaveWord}
        disableAutoFocus={isMobile}
        languageCode={selectedLanguage}
        playPronunciation={(word, lang) => {
          console.log('Play pronunciation for:', word, lang);
        }}
      />

      {/* Audio Player */}
      {audioPlayerState.isOpen && (
        <AudioPlayer
          audioSrc={audioPlayerState.audioSrc}
          title={audioPlayerState.title}
          onClose={closeAudioPlayer}
          autoPlay={true}
          position="bottom"
        />
      )}

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        message={notification.message}
      />
    </Box>
  );
};

export default BreadcrumbsStoryDetail;